import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Tag from '@atoms/Tag/Tag';
import { fadeUp } from '@config/animations.config';
import { IImage } from '@models/IImage';
import { IPricingData } from '@models/IPricingData';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { motion } from 'framer-motion';

interface Props {
  readonly name: string;
  readonly skuCode: string;
  readonly url: string;
  readonly thumbnail?: IImage;
  readonly pricingData?: IPricingData;
  readonly activeVariation: boolean;
  readonly outOfStockLabel: string;
  readonly dataTestId?: string;
}

const ProductDetailsVariantCard = ({
  name,
  skuCode,
  url,
  thumbnail,
  pricingData,
  outOfStockLabel,
  activeVariation,
  dataTestId = 'Product-Details-Variant-Card',
}: Props) => (
  <motion.a
    variants={fadeUp}
    key={skuCode}
    className={classNames(
      'relative flex h-full flex-col items-center rounded-xl border-2 bg-white p-2.5 pb-7 text-center text-black-100  hover:cursor-pointer hover:border-yellow',
      {
        'border-yellow': activeVariation,
      }
    )}
    href={url}
    onClick={() => {
      updateDataLayer({
        event: 'productModification',
        content_type: 'configuration',
        selection: skuCode,
      });
    }}
    data-testid={dataTestId}
  >
    {(pricingData?.status === ProductStockStatusEnum.OutOfStock ||
      activeVariation) && (
      <div className="flex w-full flex-col items-start justify-between gap-y-2 lg:flex-row lg:items-center">
        {pricingData?.status === ProductStockStatusEnum.OutOfStock && (
          <Tag
            className="bg-grey-100 text-black-100"
            pointerStates={false}
            data-testid={`${dataTestId}-Out-Of-Stock-Tag`}
          >
            <div className="flex items-center">
              <Icon name="cancel" className="mr-2" size={IconSizeEnum.md} />
              <span className="text-xs leading-20">{outOfStockLabel}</span>
            </div>
          </Tag>
        )}

        {activeVariation && (
          <span className="ml-auto rounded-full bg-yellow">
            <Icon name="check" colorClassName="text-white" />
          </span>
        )}
      </div>
    )}

    {thumbnail && (
      <div className="my-5 px-2.5">
        <Image image={thumbnail} dataTestId={`${dataTestId}-Image-Thumbnail`} />
      </div>
    )}

    <p className="body-three mt-auto" data-testid={`${dataTestId}-Name`}>
      {name}
    </p>

    {(pricingData?.formattedPrice || pricingData?.formattedCalculatedPrice) &&
      !!pricingData?.price && (
        <div className="body-four mt-8 text-center text-grey-450">
          {pricingData.amountSaved > 0 && pricingData.formattedPrice && (
            <p
              className={classNames({
                'line-through decoration-1': pricingData.formattedPrice,
              })}
              data-testid={`${dataTestId}-Formatted-Price`}
            >
              {pricingData.formattedPrice}
            </p>
          )}
          {pricingData.formattedCalculatedPrice && (
            <p data-testid={`${dataTestId}-Formatted-Calculated-Price`}>
              {pricingData.formattedCalculatedPrice}
            </p>
          )}
        </div>
      )}
  </motion.a>
);

export default ProductDetailsVariantCard;
